import React, {
  useEffect,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import {
  InsuranceDTO,
  SyncInsuranceAttachmentsRequestDTO,
} from 'dtos';
import dayjs from 'utils/dayjs';
import {
  getSubmitButtonText,
  sanitizeFileName,
} from '@populateui/utils';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  useGetPatientSummary,
  useGetPatientInsurances,
  useInsuranceMetaData,
  useSyncInsuranceAttachments,
  useUpsertInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  getInsuranceSlotConflictValidator,
} from 'utils/misc';
import InsuranceForm, {
  InsuranceDataForm,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';
import AssociatedInsuranceCardsPanel from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel';
import {
  getDemographicsFromInsurance,
  getPatientDemographics,
} from 'pages/Dashboard/utils/patient';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  serializeInsurance,
} from 'pages/Dashboard/utils/helper';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  InsuranceAttachmentFormState,
} from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel/useAssociatedInsuranceCards';

export type InsuranceDialogParams = {
  patientId: number;
  insurance?: InsuranceDTO;
  dialogHeight?: number;
};

type Props = InsuranceDialogParams & {
  open: boolean;
  onClose: () => void;
};

function serializeInsuranceAttachmentSyncRequest(
  insuranceId: number,
  patientId: number,
  attachmentState: InsuranceAttachmentFormState,
): SyncInsuranceAttachmentsRequestDTO {
  const mediaList = attachmentState.newUploadFiles.map(({ dataUrl, file, name }) => ({
    multimediaType: MultimediaType.InsuranceCard,
    dataUrl: dataUrl ?? '',
    fileName: sanitizeFileName(file?.name ?? name ?? ''),
  }));

  return {
    insuranceId,
    ensureAssociatedPatientMediaIds: attachmentState.existingMediaIds,
    newUploads: mediaList.length > 0
      ? {
        patientId,
        mediaList,
      }
      : undefined,
  };
}

export default function InsuranceDialog({
  insurance,
  open,
  onClose,
  dialogHeight,
  patientId,
}: Props) {
  const {
    data: patientSummary,
    isLoading: isLoadingPatientSummary,
  } = useGetPatientSummary(patientId ?? 0);

  const {
    data: patientInsurances,
  } = useGetPatientInsurances(patientId ?? 0);

  const isNewInsurance = isNil(insurance);
  const { mutateAsync: upsertInsurances } = useUpsertInsurances();
  const { mutateAsync: syncInsuranceAttachments } = useSyncInsuranceAttachments();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  const defaultInsuranceOwnerType = isNewInsurance
    ? 'Self' : insurance?.insuranceOwnerRelationshipType;

  const {
    relationshipTypesByName,
    restrictedSlotSet,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();

  const validateInsuranceSlotConflict = useMemo(
    () => getInsuranceSlotConflictValidator(
      patientInsurances ?? [],
      restrictedSlotSet,
    ),
    [patientInsurances, restrictedSlotSet],
  );

  const {
    isSubscriberPatient,
    isEmployerInsurance,
  } = relationshipTypesByName[defaultInsuranceOwnerType ?? ''] ?? {};

  const defaultData = {
    ...insurance,
    attachmentState: {
      existingMediaIds: [],
      newUploadFiles: [],
    },
    insuranceOwnerRelationshipType: defaultInsuranceOwnerType,
    insuranceOwnerDOB: dayjs(insurance?.insuranceOwnerDOB),
    effectiveFrom: dayjs(insurance?.effectiveFrom ?? undefined),
    effectiveTill: isNil(insurance?.effectiveTill) ? null : dayjs(insurance?.effectiveTill),
    demographics: isSubscriberPatient || isEmployerInsurance
      ? getPatientDemographics(patientSummary ?? {})
      : getDemographicsFromInsurance(insurance ?? {}),
  } as InsuranceDataForm;

  const formProps = useForm<InsuranceDataForm>({
    mode: 'onChange',
    defaultValues: defaultData,
  });

  const {
    formState: { isValid, isSubmitting },
    handleSubmit,
    setValue,
  } = formProps;

  useEffect(() => {
    if (!isNil(patientSummary) && (isSubscriberPatient || isEmployerInsurance)) {
      setValue('demographics', getPatientDemographics(patientSummary ?? {}));
    }
  }, [patientSummary, isSubscriberPatient, isEmployerInsurance]);

  const onSave = async (formData: InsuranceDataForm) => {
    try {
      const conflictingInsurance = validateInsuranceSlotConflict({
        currentInsuranceId: formData?.insuranceId,
        selectedInsuranceSlot: formData?.insuranceSlot,
        isActiveInsurance: !(formData?.inactive ?? false),
      });

      const saveInsurance = async () => {
        const insuranceOwnerRelationshipType = relationshipTypesByName[formData.insuranceOwnerRelationshipType ?? ''];
        const insurance = serializeInsurance(
          formData ?? {},
          patientSummary ?? {},
          insuranceOwnerRelationshipType,
        );

        const updatedInsurance = await upsertInsurances({
          patientId: patientId ?? 0,
          insurance,
        });
        const savedInsuranceId = updatedInsurance?.insuranceId ?? 0;

        if (savedInsuranceId <= 0) {
          throw new Error('Unable to resolve saved insurance ID');
        }

        const syncPayload = serializeInsuranceAttachmentSyncRequest(
          savedInsuranceId,
          patientId ?? 0,
          formData.attachmentState,
        );

        try {
          await syncInsuranceAttachments(syncPayload);
        } catch (error) {
          notify({
            variant: 'error',
            message: 'Insurance was saved but attachments failed to sync. Please try again.',
          });
          throw error;
        }

        notify({
          variant: 'success',
          message: `Insurance was ${isNewInsurance ? 'added' : 'updated'} successfully`,
        });

        onClose();
        insurancesEventChannel.emit('onPatientInsuranceUpdated', patientId, updatedInsurance ?? {});
      };

      if (!isNil(conflictingInsurance)) {
        const slotLabel = !isNil(formData?.insuranceSlot)
          ? getInsuranceSlotLabel(formData.insuranceSlot)
          : undefined;
        confirm({
          content: (
            <div>
              Are you sure you want to mark
              {' '}
              <span className="font-semibold">{formData?.insuranceName}</span>
              {' '}
              as
              {' '}
              <span className="font-semibold">{`Active ${slotLabel}`}</span>
              ?
              <div className="mt-2">
                <span className="font-semibold">{conflictingInsurance.insuranceName}</span>
                {' is currently set as '}
                <span className="font-semibold">{slotLabel}</span>
                {' and will be moved to '}
                <span className="font-semibold">Other</span>
                .
              </div>
            </div>
          ),
          onConfirm: async () => {
            try {
              await saveInsurance();
              closeConfirm();
            } catch (error) {
              console.error(error);
            }
          },
          title: isNewInsurance ? 'Add Insurance' : 'Update Insurance',
          confirmText: 'Confirm',
        });
        return;
      }

      await saveInsurance();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="min-w-[40rem] md:w-[44rem] lg:!w-[64rem] xl:!w-[72rem] transition-all duration-300 !space-y-0 overflow-y-hidden !p-0"
      titleClassName="p-5"
      title={isNewInsurance ? 'Add Insurance' : 'Update Insurance'}
      minHeight={dialogHeight}
    >
      <FormProvider {...formProps}>
        <div className="flex min-h-0 flex-1 flex-col max-h-[80vh]">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden px-5 lg:flex-row lg:items-stretch">
            <div className="flex min-w-0 w-full flex-1">
              <InsuranceForm
                patientSummary={patientSummary ?? {}}
                defaultValues={defaultData}
                isLoading={isLoadingPatientSummary}
              />
            </div>
            <AssociatedInsuranceCardsPanel
              className="h-[16rem] shrink-0 lg:h-auto lg:min-h-0 lg:shrink lg:self-stretch"
              insuranceId={insurance?.insuranceId}
              patientId={patientId}
              open={open}
            />
          </div>
          <div className="flex shrink-0 flex-row-reverse justify-between p-5">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSave)}
              >
                {getSubmitButtonText(isNewInsurance, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
